import type {
  ActingParty,
  ConveyedPersonAction,
  PersonRecordCreators,
} from "../types/involvement.js";
import { organisationsThatCreatedPersonRecords } from "./organisationsThatCreatedPersonRecords.js";
import { personActionsPermittedByRole } from "./personActionsPermittedByRole.js";

/** What having created one of a person's records conveys, before a role narrows it. */
const creationActions: readonly ConveyedPersonAction[] = ["read", "update"];

/**
 * What having created one of a person's records conveys to a party acting for
 * the creating organisation, as far as its roles permit.
 *
 * Deliberately *not* `personActionsFromInvolvementKindInRole("created_by", role)`.
 * That involvement is the narrower thing, provenance plus allocating the first
 * responsible user, and reading this through it would take correcting the
 * person away from every organisation that created a person record with no
 * involvements recorded.
 *
 * One statement for the rule the server applies and the page's prediction of
 * it, so the two cannot disagree and removing it removes both.
 *
 * TODO(haven-involvements): delete this once enrolment writes an involvement
 * for every person record, when creating one stops conveying anything.
 */
export const personActionsFromCreation = (
  person: PersonRecordCreators,
  party: Pick<ActingParty, "organisationId" | "roles">,
): ConveyedPersonAction[] => {
  if (
    !organisationsThatCreatedPersonRecords(person).includes(
      party.organisationId,
    )
  ) {
    return [];
  }

  const permitted = new Set(
    party.roles.flatMap((role) => personActionsPermittedByRole(role)),
  );

  return creationActions.filter((action) => permitted.has(action));
};
